import { globalHistory } from '@reach/router';
import { GatsbyBrowser } from 'gatsby';

import {
  recordLandedOnPage,
  recordSecondsOnPage,
  recordSecondsToLoad,
  recordUserBounce,
  recordVisitedPage
} from './src/utils/vanityMetrics';

declare global {
  interface Window {
    optimizely: object[];
    optimizelyActionApplied: boolean | null;
  }
}

let landed = false;
let leaving: boolean;

const shouldUpdateScroll: GatsbyBrowser['shouldUpdateScroll'] = ({
  routerProps: {
    location: { pathname }
  }
}) => {
  if (pathname.indexOf('/resource-center') !== -1) {
    return false;
  }

  return true;
};

const onClientEntry: GatsbyBrowser['onClientEntry'] = async () => {
  if (typeof IntersectionObserver === 'undefined') {
    // @ts-ignore
    await import('intersection-observer');
  }
  const preloadChart = document.createElement('iframe');
  preloadChart.style.display = 'none';
  preloadChart.src = 'https://www.lucidchart.com/documents/preloadChart';

  const loadPreloadScript = () => {
    document.getElementsByTagName('body')[0].appendChild(preloadChart);
    window.removeEventListener('scroll', loadPreloadScript);
  };
  window.addEventListener('scroll', loadPreloadScript);

  window.addEventListener('load', () => {
    window.setTimeout(() => {
      recordSecondsToLoad();
    });
  });

  window.addEventListener('beforeunload', () => {
    recordSecondsOnPage(window.location.pathname);
    if (landed === true && leaving === true) {
      recordUserBounce(window.location.pathname);
    }
  });

  window.optimizely = window.optimizely || [];

  window.optimizely.push({
    type: 'addListener',
    filter: {
      type: 'action',
      name: 'applied'
    },
    handler: () => {
      window.optimizelyActionApplied = true;
    }
  });
};

const onInitialClientRender: GatsbyBrowser['onInitialClientRender'] = () => {
  leaving = true;
  if (document.referrer.indexOf('www.lucidchart.com') === -1) {
    landed = true;
    recordLandedOnPage(window.location.pathname);
  }

  if (window.location.search.indexOf('showEditLink=1') !== -1) {
    window.localStorage.setItem('showEditLink', '1');
  }

  /**
   * This is a workaround for a bug in Gatsby
   *
   * See https://github.com/gatsbyjs/gatsby/issues/8357 for more details
   */
  try {
    // @ts-ignore
    globalHistory._onTransitionComplete();
  } catch (e) {
    console.error(e);
  }
};

const onRouteUpdate: GatsbyBrowser['onRouteUpdate'] = ({ prevLocation }) => {
  landed = false;
  leaving = false;

  let { pathname } = window.location;
  if (pathname === '/' || pathname === 'pages/') {
    pathname = 'homepage';
  }

  recordVisitedPage(pathname);

  Array.from(document.querySelectorAll('a')).forEach(link => {
    link.addEventListener('click', () => {
      if (link.href.indexOf('lucidchart.com') !== -1) {
        leaving = false;
      }
    });
  });

  Array.from(document.querySelectorAll('.js-eo-form')).forEach(form => {
    form.addEventListener('submit', () => {
      leaving = false;
    });
  });

  try {
    window.optimizely.push({
      type: 'activate'
    });
  } catch (error) {
    console.error('Unable to report to Optimizely on route change.', error);
  }

  if (
    prevLocation &&
    !(prevLocation as Location).pathname.includes('/contact/')
  ) {
    // set session storage so we can use it on single page marketo form
    try {
      sessionStorage.setItem(
        'prevLocation',
        (prevLocation as Location).pathname
      );
    } catch {
      // no-op
    }
  }
};

export {
  onClientEntry,
  onRouteUpdate,
  onInitialClientRender,
  shouldUpdateScroll
};
